'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import type {
  Category,
  SubCategoriesEntity,
} from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Breadcrumbs } from '../homepage/breadcrumbs';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { MdOutlineArrowCircleRight } from 'react-icons/md';
import { GoArrowRight } from 'react-icons/go';
import { CATEGORY_INFO_ID, hasCategoryInfo } from './category-info';

export function CategoryHeader({ category }: { category: Category }) {
  const { t } = useTranslations();

  return (
    <section className={cn('custom-section', 'py-8 sm:py-10 lg:py-12')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-8 md:flex-row md:justify-between',
        )}
      >
        <div className="flex w-full flex-col items-center justify-center gap-6 sm:items-start sm:justify-start">
          {!!category?.breadcrumb && (
            <Breadcrumbs
              breadcrumb={category.breadcrumb}
              className={{
                list: 'items-center justify-center sm:items-start sm:justify-start',
                wrapper: 'items-center justify-center sm:items-start sm:justify-start',
              }}
            />
          )}
          <Heading
            title={category.name}
            tag="h1"
            className={{
              container:
                'items-center justify-center text-center sm:items-start sm:justify-start sm:text-left',
            }}
          />
          {hasCategoryInfo(category) && (
            <Button asChild variant={'none'} size={'none'}>
              <a href={`#${CATEGORY_INFO_ID}`}>
                <MdOutlineArrowCircleRight size={18} aria-hidden /> {t('text792')}
              </a>
            </Button>
          )}
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {category.sub_categories?.map(child => (
              <CategoryCard subCategory={child} key={child?.slug} />
            ))}
          </div>
        </div>
        {/* {category.sub_categories && category?.sub_categories.length > 0 && (
          <div className="w-full md:max-w-[688px]">
            <Carousel
              className={{
                wrapper: 'w-full',
                container: 'w-full gap-3',
                main: 'w-full',
                slide: 'max-w-[140px] sm:max-w-[170px] lg:max-w-[230px]',
              }}
              slides={category?.sub_categories?.map(subCategory => (
                <CategoryCard subCategory={subCategory} key={subCategory?.slug} />
              ))}
            />
          </div>
        )} */}
      </div>
    </section>
  );
}

function CategoryCard({ subCategory }: { subCategory: SubCategoriesEntity }) {
  return (
    <Link
      href={subCategory?.slug ?? ''}
      prefetchOnHover
      className={cn(
        'group relative flex w-full items-center justify-between gap-5 overflow-hidden rounded-[4px] border p-2 transition-all hover:border-primary',
      )}
    >
      {subCategory?.icon?.url && (
        <Image
          src={subCategory?.icon?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(subCategory?.icon, subCategory?.name)}
          width={0}
          height={0}
          className="max-h-16 min-h-16 min-w-16 max-w-16 shrink-0 rounded-[3px] bg-[#F4F4F4] object-contain p-1"
          sizes="64px"
        />
      )}

      <Heading
        tag="h2"
        title={subCategory.name}
        className={{ title: 'h3 pr-4 font-heading', container: 'w-full flex-1' }}
      />
      <GoArrowRight size={24} className="mr-2 max-h-6 min-h-6 min-w-6 max-w-6" />
    </Link>
  );
}
